import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../services/AxiosInstance';
import './MyPlanPage.css';

const STATUS_TONES = {
    ACTIVE: 'success', PENDING_PAYMENT: 'warning', OVERDUE: 'danger',
    BLOCKED: 'danger', EXEMPT: 'info', CANCELED: 'neutral', NONE: 'neutral',
};

const MyPlanPage = () => {
    const { t, i18n } = useTranslation();
    const [plano, setPlano] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(false);
    const [tentativa, setTentativa] = useState(0);

    useEffect(() => {
        let ativo = true;
        setCarregando(true);
        setErro(false);
        axiosInstance.get('/saas-billing/my-plan')
            .then(({ data }) => { if (ativo) setPlano(data); })
            .catch(() => { if (ativo) setErro(true); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [tentativa]);

    const status = STATUS_TONES[plano?.contractStatus] ? plano.contractStatus : 'NONE';
    // Limite nulo representa ausência de teto no contrato, não uma quantidade zero.
    const formatarLimite = limite => (limite == null ? t('my_plan.unlimited') : String(limite));
    const valor = plano?.price == null ? '—' : new Intl.NumberFormat(i18n.resolvedLanguage === 'en' ? 'en-US' : 'pt-BR', {
        style: 'currency', currency: 'BRL',
    }).format(plano.price);
    const vencimento = plano?.nextDueDate ? moment(plano.nextDueDate).format('DD/MM/YYYY') : '—';

    return (
        <div className="my-plan">
            <header className="my-plan__header">
                <div>
                    <span className="my-plan__eyebrow">COLMEIA</span>
                    <h1>{t('my_plan.title')}</h1>
                    <p>{t('my_plan.subtitle')}</p>
                </div>
            </header>

            {carregando ? (
                <div className="my-plan__state" role="status"><Spinner animation="border" /><span>{t('my_plan.loading')}</span></div>
            ) : erro ? (
                <div className="my-plan__state">
                    <div className="my-plan__state-icon"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /></div>
                    <h2>{t('my_plan.error_title')}</h2>
                    <p>{t('my_plan.error_description')}</p>
                    <Button variant="primary" onClick={() => setTentativa(value => value + 1)}>{t('my_plan.retry')}</Button>
                </div>
            ) : !plano?.planName ? (
                <div className="my-plan__state">
                    <div className="my-plan__state-icon"><i className="fa-solid fa-file-circle-question" aria-hidden="true" /></div>
                    <h2>{t('my_plan.empty_title')}</h2>
                    <p>{t('my_plan.empty_description')}</p>
                </div>
            ) : (
                <div className="my-plan__grid">
                    <section className="my-plan__card my-plan__overview" aria-labelledby="my-plan-name">
                        <div className="my-plan__card-top">
                            <div className="my-plan__icon"><i className="fa-solid fa-crown" aria-hidden="true" /></div>
                            <span className={`my-plan__status my-plan__status--${STATUS_TONES[status]}`}><span />{t(`my_plan.status.${status}`)}</span>
                        </div>
                        <div className="my-plan__plan-info">
                            <span className="my-plan__label">{t('my_plan.current')}</span>
                            <h2 id="my-plan-name">{plano.planName}</h2>
                            {plano.planDescription && <p>{plano.planDescription}</p>}
                        </div>
                        <div className="my-plan__price"><strong>{valor}</strong>{plano.price != null && plano.cycle && <span>{t(`my_plan.cycles.${plano.cycle}`, { defaultValue: '' })}</span>}</div>
                        <div className="my-plan__divider" />
                        <h3>{t('my_plan.includes')}</h3>
                        <div className="my-plan__limits">
                            <div className="my-plan__limit"><i className="fa-solid fa-store" aria-hidden="true" /><div><span>{t('my_plan.branches')}</span><strong>{formatarLimite(plano.maxBranches)}</strong></div></div>
                            <div className="my-plan__limit"><i className="fa-solid fa-users" aria-hidden="true" /><div><span>{t('my_plan.professionals')}</span><strong>{formatarLimite(plano.maxProfessionals)}</strong></div></div>
                        </div>
                    </section>
                    <section className="my-plan__card my-plan__payment" aria-labelledby="my-plan-payment">
                        <div className="my-plan__payment-heading"><div className="my-plan__icon my-plan__icon--light"><i className="fa-solid fa-wallet" aria-hidden="true" /></div><div><h2 id="my-plan-payment">{t('my_plan.payment')}</h2><p>{t('my_plan.payment_subtitle')}</p></div></div>
                        <div className="my-plan__due"><span>{t('my_plan.next_due_date')}</span><strong>{vencimento}</strong></div>
                        <div className={`my-plan__notice ${plano.hasOpenCharge ? 'my-plan__notice--open' : ''}`}>
                            <i className={`fa-solid ${plano.hasOpenCharge ? 'fa-circle-exclamation' : 'fa-circle-check'}`} aria-hidden="true" />
                            <span>{plano.hasOpenCharge ? (plano.paymentLink ? t('my_plan.open_charge_hint') : t('my_plan.open_charge_no_link')) : t('my_plan.no_open_charge')}</span>
                        </div>
                        {/* Só oferecemos pagamento quando a cobrança aberta possui um link válido. */}
                        {plano.hasOpenCharge && plano.paymentLink && <Button className="my-plan__pay" variant="primary" href={plano.paymentLink} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-barcode" aria-hidden="true" /> {t('my_plan.pay_now')} <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" /></Button>}
                        <div className="my-plan__help"><i className="fa-regular fa-circle-question" aria-hidden="true" /><p>{t('my_plan.change_hint')}</p></div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default MyPlanPage;
